import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../api/client.js";
import { cn } from "../lib/utils.js";

/**
 * Autocomplete over OpenRouter's full model catalog. Free text still commits on
 * blur/Enter — the project's model column is an unvalidated string, and the
 * catalog can lag behind a model OpenRouter just added.
 *
 * The dropdown renders through a portal: every caller so far sits inside a
 * StripField, whose `truncate` wrapper clips anything absolutely positioned
 * within it, dropdown included.
 */
export function ModelPicker({
  value,
  onChange,
  className,
}: {
  value: string;
  onChange: (model: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(value);
  const [highlight, setHighlight] = useState(0);
  const [rect, setRect] = useState<{ left: number; top: number; width: number } | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => setQuery(value), [value]);

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  useEffect(() => {
    if (!open) return;
    function updateRect() {
      const box = rootRef.current?.getBoundingClientRect();
      if (box) setRect({ left: box.left, top: box.bottom + 4, width: box.width });
    }
    updateRect();
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
    };
  }, [open]);

  const models = useQuery({ queryKey: ["models"], queryFn: api.models, staleTime: 60 * 60 * 1000 });

  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = models.data ?? [];
    const filtered = q ? all.filter((m) => m.id.toLowerCase().includes(q) || m.name.toLowerCase().includes(q)) : all;
    return filtered.slice(0, 50);
  }, [models.data, query]);

  function commit(model: string) {
    if (model.trim()) onChange(model.trim());
    setQuery(model.trim() || value);
    setOpen(false);
  }

  return (
    <div ref={rootRef} className={cn("relative", className)}>
      <input
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setHighlight(0);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlight((h) => Math.min(h + 1, matches.length - 1));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlight((h) => Math.max(h - 1, 0));
          } else if (e.key === "Enter") {
            e.preventDefault();
            commit(matches[highlight]?.id ?? query);
          } else if (e.key === "Escape") {
            setQuery(value);
            setOpen(false);
          }
        }}
        onBlur={() => {
          // A plain click on a dropdown item fires blur first; give the
          // mousedown handler above a chance to register the selection.
          setTimeout(() => commit(query), 0);
        }}
        placeholder="Modell suchen…"
        role="combobox"
        aria-expanded={open}
        aria-autocomplete="list"
        className="flex h-9 w-full rounded-full border border-border bg-card px-3.5 py-1 font-mono text-xs transition-colors placeholder:text-muted-foreground placeholder:font-sans hover:border-input focus:border-foreground focus:outline-none disabled:opacity-50"
      />
      {open &&
        rect &&
        createPortal(
          <div
            style={{ position: "fixed", left: rect.left, top: rect.top, width: Math.max(rect.width, 256) }}
            className="z-50 max-h-64 overflow-y-auto rounded-xl border border-border bg-popover p-1 text-popover-foreground shadow-[0_6px_20px_rgb(0_0_0/0.12)]"
          >
            {models.isLoading ? (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">Modelle werden geladen…</p>
            ) : models.isError ? (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">
                Katalog nicht erreichbar — freier Text wird trotzdem übernommen.
              </p>
            ) : matches.length === 0 ? (
              <p className="px-2 py-1.5 text-xs text-muted-foreground">Kein Treffer — Enter übernimmt „{query}“.</p>
            ) : (
              matches.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => commit(m.id)}
                  className={cn(
                    "flex w-full flex-col items-start gap-0.5 rounded-sm px-2 py-1.5 text-left text-xs outline-none",
                    i === highlight
                      ? "bg-accent text-accent-foreground"
                      : "hover:bg-accent hover:text-accent-foreground",
                  )}
                >
                  <span className="font-mono">{m.id}</span>
                  <span className="text-[11px] text-muted-foreground">{m.name}</span>
                </button>
              ))
            )}
          </div>,
          document.body,
        )}
    </div>
  );
}
